import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ALERT_HAS_THRESHOLD,
  ALERT_SUBJECT_OF,
  DEFAULT_ALERT_RULE_SPEC,
  HEALTH_ALERT_TYPES,
  type AlertChannel,
  type AlertRule,
  type AlertRuleScope,
  type EffectiveAlertRule,
  type HealthAlertRecord,
  type HealthAlertType,
} from "@shared/types/foyer";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { useAuth } from "../auth/AuthContext";
import { tokens } from "../theme";
import { PageContainer, PageHeader } from "../components/PageHeader";
import HelpBadge from "../components/HelpBadge";
import StaffPushOptIn from "../components/StaffPushOptIn";

/**
 * Health alerting rules (Phase 5).
 *
 * THE PAGE IS A SCOPE PICKER PLUS ONE CARD PER RULE TYPE, deliberately. Rules resolve
 * most-specific-wins (premise > company > global), so the question an operator
 * actually has is "what fires HERE?" - not "list every rule row in the system".
 * Picking a scope first and then showing every type with an explicit
 * inherited/overridden badge answers that directly; a flat CRUD list of rows
 * would make the reader compute precedence in their head.
 */

const TYPE_LABEL: Record<HealthAlertType, string> = {
  device_offline: "Device offline",
  never_reported: "Never reported",
  version_drift: "Version drift",
  failed_commands: "Failed commands",
  calling_unavailable: "Calling unavailable",
  playback_failing: "Playback failing",
  signage_stalled: "Signage stalled",
};

const TYPE_BLURB: Record<HealthAlertType, string> = {
  device_offline:
    "A device that has reported before goes quiet for longer than the threshold.",
  never_reported:
    "A device paired this long ago has still never sent a heartbeat - usually a bad install.",
  version_drift:
    "This many devices at the site are not on the build most of its devices run.",
  failed_commands:
    "This many commands failed or expired at the site inside the health look-back window.",
  calling_unavailable:
    "The site has video calling switched on while the server has no media plane configured - every call it offers fails, and the TV hides the tile. There is no threshold: it is on or it is not.",
  playback_failing:
    "This many playback failures were counted across the site today. A room whose player refuses every licence is otherwise invisible - it reports online and heartbeats happily, and the first signal is a guest at the front desk. Off by default: a site with a bad evening uplink will stall legitimately, so decide what normal looks like here first.",
  signage_stalled:
    "A signage screen's own heartbeat says it is blocked (nothing can play) or visibly stalled (frozen mid-clip). There is no threshold: it is happening right now, or it is not. Off by default: a wedged decoder can recover on its own before the next sweep, so decide what normal looks like here first, same as playback failing.",
};

/** The unit each type's threshold is counted in. */
const TYPE_UNIT: Record<HealthAlertType, string> = {
  device_offline: "minutes",
  never_reported: "minutes",
  version_drift: "devices",
  failed_commands: "commands",
  calling_unavailable: "",
  playback_failing: "failures",
  signage_stalled: "",
};

const SOURCE_LABEL: Record<EffectiveAlertRule["source"], string> = {
  default: "Built-in default",
  global: "All companies",
  company: "Company",
  premise: "This premise",
};

/** Split a free-text recipients box (newline/comma separated) into emails. */
function parseRecipients(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The scope the page is currently editing, encoded as one select value. */
type ScopeValue =
  | { kind: "global" }
  | { kind: "company"; companyId: string }
  | { kind: "premise"; premiseId: string };

function encodeScope(s: ScopeValue): string {
  if (s.kind === "global") return "global";
  if (s.kind === "company") return `company:${s.companyId}`;
  return `premise:${s.premiseId}`;
}

function decodeScope(v: string): ScopeValue {
  if (v.startsWith("company:")) {
    return { kind: "company", companyId: v.slice("company:".length) };
  }
  if (v.startsWith("premise:")) {
    return { kind: "premise", premiseId: v.slice("premise:".length) };
  }
  return { kind: "global" };
}

/** Editable state for one rule card. Strings so partial input doesn't fight. */
interface Draft {
  enabled: boolean;
  threshold: string;
  channels: AlertChannel[];
  recipients: string;
}

function draftFrom(eff: EffectiveAlertRule): Draft {
  return {
    enabled: eff.enabled,
    threshold: String(eff.threshold),
    channels: eff.channels.length > 0 ? eff.channels : ["email"],
    recipients: eff.recipients.join("\n"),
  };
}

export default function HealthAlertsPage() {
  const { user } = useAuth();
  const isAdmin = user?.type === "admin";

  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [alerts, setAlerts] = useState<HealthAlertRecord[]>([]);
  const [scope, setScope] = useState<ScopeValue>({ kind: "global" });
  const [effective, setEffective] = useState<EffectiveAlertRule[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState<HealthAlertType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);

  // ── Load ─────────────────────────────────────────────────────────────────

  const loadRules = useCallback(async () => {
    const { rules: rs } = await foyerApi.listAlertRules();
    setRules(rs);
    return rs;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ps, rs, al] = await Promise.all([
          foyerApi.listPremises(),
          foyerApi.listAlertRules(),
          foyerApi.listAlerts({ limit: 50 }),
        ]);
        if (cancelled) return;
        setPremises(ps);
        setRules(rs.rules);
        setAlerts(al.alerts);
        // The company list is admin-only; a tenant's own company is implied by
        // its token, so a failure here must not blank the page.
        if (isAdmin) {
          try {
            setCompanies(await foyerApi.listCompanies());
          } catch {
            /* non-fatal - the company tier just won't be selectable */
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load rules.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  /**
   * The effective rule set for the selected scope. For a premise the server
   * computes it (so precedence lives in exactly one place); for the global and
   * company tiers there is no single premise to resolve against, so the card
   * shows that tier's own rows falling back to the built-in defaults.
   */
  const refreshEffective = useCallback(
    async (s: ScopeValue, ruleSet: AlertRule[]) => {
      if (s.kind === "premise") {
        const res = await foyerApi.effectiveAlertRules(s.premiseId);
        return res.effective;
      }
      return HEALTH_ALERT_TYPES.map((type): EffectiveAlertRule => {
        const own = ruleSet.find(
          (r) =>
            r.type === type &&
            r.scope === s.kind &&
            (s.kind === "global" || r.companyId === s.companyId)
        );
        if (own) {
          return {
            type,
            enabled: own.enabled,
            threshold: own.threshold,
            channels: own.channels,
            recipients: own.recipients,
            source: s.kind,
            ruleId: own.ruleId,
          };
        }
        const fallback = ruleSet.find(
          (r) => r.type === type && r.scope === "global"
        );
        if (s.kind === "company" && fallback) {
          return {
            type,
            enabled: fallback.enabled,
            threshold: fallback.threshold,
            channels: fallback.channels,
            recipients: fallback.recipients,
            source: "global",
            ruleId: fallback.ruleId,
          };
        }
        // Read the built-in defaults from the shared spec rather than
        // restating them: a new rule type that ships ON would otherwise show
        // as OFF here until somebody remembered this branch. `threshold: null`
        // in the spec means "inherit settings.alertOfflineMinutes"; the server
        // substitutes the live value, this preview shows its default.
        const spec = DEFAULT_ALERT_RULE_SPEC[type];
        return {
          type,
          enabled: spec.enabled,
          threshold: spec.threshold ?? 60,
          channels: ["email"],
          recipients: [],
          source: "default",
        };
      });
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const eff = await refreshEffective(scope, rules);
        if (cancelled) return;
        setEffective(eff);
        setDrafts(
          Object.fromEntries(eff.map((e) => [e.type, draftFrom(e)])) as Record<
            string,
            Draft
          >
        );
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to resolve rules.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, rules, refreshEffective]);

  // ── Mutations ────────────────────────────────────────────────────────────

  /** The rule row for this type AT the selected scope, if one exists. */
  const ownRuleFor = useCallback(
    (type: HealthAlertType): AlertRule | undefined =>
      rules.find(
        (r) =>
          r.type === type &&
          r.scope === scope.kind &&
          (scope.kind === "global" ||
            (scope.kind === "company"
              ? r.companyId === scope.companyId
              : r.premiseId === scope.premiseId))
      ),
    [rules, scope]
  );

  async function save(type: HealthAlertType) {
    const draft = drafts[type];
    if (!draft) return;
    setSavingType(type);
    try {
      await foyerApi.saveAlertRule({
        type,
        scope: scope.kind as AlertRuleScope,
        ...(scope.kind === "company" ? { companyId: scope.companyId } : {}),
        ...(scope.kind === "premise" ? { premiseId: scope.premiseId } : {}),
        enabled: draft.enabled,
        threshold: Number(draft.threshold) || null,
        channels: draft.channels,
        recipients: parseRecipients(draft.recipients),
      });
      await loadRules();
      setSnack({ severity: "success", text: `${TYPE_LABEL[type]} saved.` });
    } catch (e) {
      setSnack({
        severity: "error",
        text: e instanceof Error ? e.message : "Save failed.",
      });
    } finally {
      setSavingType(null);
    }
  }

  async function resetToInherited(type: HealthAlertType) {
    const own = ownRuleFor(type);
    if (!own) return;
    setSavingType(type);
    try {
      await foyerApi.deleteAlertRule(own.ruleId);
      await loadRules();
      setSnack({
        severity: "success",
        text: `${TYPE_LABEL[type]} now inherits again.`,
      });
    } catch (e) {
      setSnack({
        severity: "error",
        text: e instanceof Error ? e.message : "Reset failed.",
      });
    } finally {
      setSavingType(null);
    }
  }

  function patch(type: HealthAlertType, next: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [type]: { ...d[type], ...next } }));
  }

  // ── Derived ──────────────────────────────────────────────────────────────

  const premiseOptions = useMemo(
    () =>
      [...premises].sort((a, b) => a.premiseName.localeCompare(b.premiseName)),
    [premises]
  );

  const openAlerts = useMemo(
    () => alerts.filter((a) => a.status === "open"),
    [alerts]
  );

  const premiseName = useCallback(
    (slug: string) =>
      premises.find((p) => p.premiseId === slug)?.premiseName ?? slug,
    [premises]
  );

  const scopeLabel =
    scope.kind === "global"
      ? "all companies"
      : scope.kind === "company"
        ? (companies.find((c) => c.id === scope.companyId)?.name ??
          "this company")
        : premiseName(scope.premiseId);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Monitoring"
        title="Health alerting"
        subtitle={`Rules currently shown for ${scopeLabel}.`}
        help="Rules resolve most-specific-wins: a premise rule beats its company's, which beats the all-companies rule. Exactly one rule is ever in force per premise and type, so nobody is notified twice for the same condition."
      />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* ── Scope picker ───────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
        <HelpBadge title="Pick the level you want to configure. Editing a premise here creates an override that applies to that site only; editing the top level changes the fallback everyone inherits." />
        <FormControl size="small" sx={{ minWidth: 320, maxWidth: "100%" }}>
          <InputLabel id="alert-scope-label">Configure rules for</InputLabel>
          <Select
            labelId="alert-scope-label"
            label="Configure rules for"
            value={encodeScope(scope)}
            onChange={(e) => setScope(decodeScope(e.target.value))}
            data-testid="alert-scope-select"
          >
            {isAdmin && (
              <MenuItem value="global">All companies (default)</MenuItem>
            )}
            {isAdmin && companies.length > 0 && (
              <ListSubheader>Companies</ListSubheader>
            )}
            {isAdmin &&
              companies.map((c) => (
                <MenuItem key={c.id} value={`company:${c.id}`}>
                  {c.name}
                </MenuItem>
              ))}
            {!isAdmin && user?.company && (
              <MenuItem value={`company:${user.company}`}>
                My company (default)
              </MenuItem>
            )}
            <ListSubheader>Premises</ListSubheader>
            {premiseOptions.map((p) => (
              <MenuItem key={p.premiseId} value={`premise:${p.premiseId}`}>
                {p.premiseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {/* ── Push opt-in (per browser, gesture-gated) ───────────────────── */}
      <StaffPushOptIn />

      {/* ── Rule cards ─────────────────────────────────────────────────── */}
      {loading || !effective ? (
        <Stack spacing={1.5}>
          {HEALTH_ALERT_TYPES.map((_, i) => (
            <Box
              key={i}
              sx={{
                height: 120,
                borderRadius: 2,
                bgcolor: tokens.rowAlt,
                opacity: 0.5,
              }}
            />
          ))}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {effective.map((eff) => {
            const draft = drafts[eff.type];
            if (!draft) return null;
            const own = ownRuleFor(eff.type);
            const inherited = !own;
            const busy = savingType === eff.type;
            return (
              <Card
                key={eff.type}
                variant="outlined"
                sx={{ p: 2.5 }}
                data-testid={`alert-rule-${eff.type}`}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { sm: "center" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          {TYPE_LABEL[eff.type]}
                        </Typography>
                        <Chip
                          size="small"
                          variant={inherited ? "outlined" : "filled"}
                          color={inherited ? "default" : "primary"}
                          label={
                            inherited
                              ? `Inherited · ${SOURCE_LABEL[eff.source]}`
                              : "Overridden here"
                          }
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            ALERT_SUBJECT_OF[eff.type] === "premise"
                              ? "Per site"
                              : "Per device"
                          }
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {TYPE_BLURB[eff.type]}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.enabled}
                          disabled={busy}
                          onChange={(e) =>
                            patch(eff.type, { enabled: e.target.checked })
                          }
                          inputProps={{
                            "aria-label": `Enable ${TYPE_LABEL[eff.type]}`,
                          }}
                        />
                      }
                      label={draft.enabled ? "On" : "Off"}
                    />
                  </Stack>

                  <Divider />

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ md: "flex-start" }}
                  >
                    {/* A boolean condition gets no threshold box: offering a
                        number whose value changes nothing is worse than
                        offering none. */}
                    {ALERT_HAS_THRESHOLD[eff.type] && (
                      <TextField
                        size="small"
                        type="number"
                        label="Threshold"
                        value={draft.threshold}
                        disabled={busy || !draft.enabled}
                        onChange={(e) =>
                          patch(eff.type, { threshold: e.target.value })
                        }
                        helperText={TYPE_UNIT[eff.type]}
                        sx={{ width: 160 }}
                        inputProps={{ min: 1 }}
                      />
                    )}

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Deliver via
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={draft.channels.includes("email")}
                              disabled={busy || !draft.enabled}
                              onChange={(e) =>
                                patch(eff.type, {
                                  channels: e.target.checked
                                    ? ([
                                        ...draft.channels.filter(
                                          (c) => c !== "email"
                                        ),
                                        "email",
                                      ] as AlertChannel[])
                                    : draft.channels.filter(
                                        (c) => c !== "email"
                                      ),
                                })
                              }
                            />
                          }
                          label="Email"
                        />
                        <Tooltip title="Sends a browser notification to the recipients above who have enabled push on this page, matched by their CMS email. A recipient with no browser enabled simply gets no push - keep Email on as well unless everyone on the list has opted in.">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={draft.channels.includes("push")}
                                disabled={busy || !draft.enabled}
                                onChange={(e) =>
                                  patch(eff.type, {
                                    channels: e.target.checked
                                      ? ([
                                          ...draft.channels.filter(
                                            (c) => c !== "push"
                                          ),
                                          "push",
                                        ] as AlertChannel[])
                                      : draft.channels.filter(
                                          (c) => c !== "push"
                                        ),
                                  })
                                }
                              />
                            }
                            label="Push"
                          />
                        </Tooltip>
                      </Stack>
                    </Box>

                    <TextField
                      size="small"
                      label="Recipients"
                      value={draft.recipients}
                      disabled={busy || !draft.enabled}
                      onChange={(e) =>
                        patch(eff.type, { recipients: e.target.value })
                      }
                      multiline
                      minRows={2}
                      placeholder="ops@example.com"
                      helperText="One per line. Leave empty to use the segment lists from General settings."
                      sx={{ flex: 1, minWidth: 260 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={busy}
                      onClick={() => void save(eff.type)}
                    >
                      {inherited ? "Override here" : "Save"}
                    </Button>
                    {!inherited && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={busy}
                        onClick={() => void resetToInherited(eff.type)}
                      >
                        Reset to inherited
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ── Active alerts ──────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
        <HelpBadge title="Alerts currently open across the fleet. An alert opens once when a condition starts and resolves once when it clears - the same lifecycle that drives the notification digest." />
        <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
          Active alerts{openAlerts.length > 0 ? ` (${openAlerts.length})` : ""}
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : openAlerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing is currently alerting. Conditions are re-evaluated every
            five minutes.
          </Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {openAlerts.map((a) => (
              <Stack
                key={a.alertId}
                direction="row"
                spacing={1.5}
                sx={{ py: 1, alignItems: "center", flexWrap: "wrap" }}
              >
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={TYPE_LABEL[a.type]}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {a.roomId ??
                    a.model ??
                    a.screenId ??
                    premiseName(a.premiseId)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {premiseName(a.premiseId)}
                </Typography>
                {a.observed !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    {a.observed} {TYPE_UNIT[a.type]}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  since {new Date(a.openedAt).toLocaleString()}
                </Typography>
                {!a.notifiedOpenAt && (
                  <Tooltip title="Tracked, but no channel has carried it yet - alerting is off, no recipients are configured, this deployment has no outbound email provider, or the rule is push-only and nobody on its list has enabled push. It is held, not dropped: it goes out on the first sweep after a channel works.">
                    <Chip
                      size="small"
                      variant="outlined"
                      label="not notified"
                    />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
      >
        <Alert severity={snack?.severity ?? "success"} variant="filled">
          {snack?.text}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
